import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export function Stars({ value = 0, className = "h-4 w-4" }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${className} ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-black/15"}`}
        />
      ))}
    </span>
  );
}

export default function KitReviews({ slug }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/kits/${slug}/reviews`)
      .then((r) => {
        setData(r.data);
        if (r.data.my_review) {
          setRating(r.data.my_review.rating);
          setComment(r.data.my_review.comment || "");
        }
      })
      .catch(() => setData(null));
  }, [slug]);

  useEffect(load, [load]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/kits/${slug}/reviews`, { rating, comment });
      toast.success(data?.my_review ? "Avis mis à jour !" : "Merci pour votre avis !");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return null;

  return (
    <section className="mt-14" id="avis" data-testid="kit-reviews-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Avis clients</h2>
          {data.reviews_count > 0 ? (
            <div className="mt-1 flex items-center gap-2" data-testid="kit-reviews-summary">
              <Stars value={data.rating_avg} />
              <span className="text-sm font-semibold">{data.rating_avg}/5</span>
              <span className="text-sm text-muted-foreground">· {data.reviews_count} avis vérifié{data.reviews_count > 1 ? "s" : ""}</span>
            </div>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">Aucun avis pour le moment.</p>
          )}
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
          <ShieldCheck className="h-3 w-3" /> Achats vérifiés uniquement
        </span>
      </div>

      {(data.can_review || data.my_review) && (
        <Card className="mt-6 p-6 border-primary/20 bg-primary/5" data-testid="kit-review-form">
          <h3 className="font-display font-semibold">
            {data.my_review ? "Modifier mon avis" : "Vous avez acheté ce kit — donnez votre avis !"}
          </h3>
          <div className="mt-3 flex items-center gap-1" data-testid="kit-review-star-picker">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setRating(i)}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(0)}
                data-testid={`review-star-${i}`}
                className="p-0.5"
              >
                <Star className={`h-7 w-7 transition-colors ${i <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-black/20"}`} />
              </button>
            ))}
            <span className="ml-2 text-sm font-semibold">{rating}/5</span>
          </div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="La réparation s'est bien passée ? Le kit était complet ? (optionnel)"
            className="mt-3 bg-white"
            data-testid="kit-review-comment"
          />
          <Button onClick={submit} disabled={submitting} className="mt-3 rounded-full" data-testid="kit-review-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {data.my_review ? "Mettre à jour" : "Publier mon avis"}
          </Button>
        </Card>
      )}

      {!user && (
        <p className="mt-4 text-xs text-muted-foreground">
          <Link to="/login" className="text-primary underline">Connectez-vous</Link> après votre achat pour laisser un avis vérifié.
        </p>
      )}

      {data.items.length > 0 && (
        <div className="mt-6 space-y-3" data-testid="kit-reviews-list">
          {data.items.map((rv) => (
            <Card key={rv.id} className="p-5 border-black/10" data-testid={`kit-review-${rv.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Stars value={rv.rating} className="h-3.5 w-3.5" />
                  <span className="text-sm font-semibold">{rv.author_name}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                    <ShieldCheck className="h-3 w-3" /> Achat vérifié
                  </span>
                </div>
                {rv.created_at && (
                  <span className="text-[11px] text-muted-foreground">
                    {new Date(rv.created_at).toLocaleDateString("fr-FR")}
                  </span>
                )}
              </div>
              {rv.comment && <p className="mt-2 text-sm text-foreground leading-relaxed">{rv.comment}</p>}
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
